// Developers — the agent debug dashboard.
//
// Read-only over `agent_trace`, the tee of every LLM call every agent makes: for
// any turn, what each agent was actually FED (system prompt + context + history)
// and what it produced (text, tool calls, finish reason, cost). The one write is
// the "ask why" chat, which enqueues and answers over Pusher.
//
// Ported from the legacy console (`admin-internal`), which is never deployed to
// production, so this surface had no answer to "why did the agent do that" in the
// environment that matters. Two behaviours changed in the port, both to the house
// pattern: server state is TanStack Query rather than hand-rolled `useEffect`
// fetching, and older pages load from a button rather than an intersection
// observer, like every other list in this console.
//
// The left rail is a TWO-LEVEL drill-down, and every level is in the URL: the
// PROJECTS list (most recently active first, searched server-side), then THAT
// project's turns (newest first, keyset-paged), then the agent tabs on the right.
// Turns with no project (e.g. profile-scoped HUE designs) collapse into a
// synthetic "No project" bucket. `?project=…&turn=…&agent=…` means a specific
// trace is a link you can paste to someone, which is most of the value of a
// debugging tool.
//
// The FILTERS cut ACROSS that drill-down rather than living inside it. "Show me
// SCOUT" is a question about turns, not about one project, so picking an agent
// (or a date range) at the top level replaces the project index with the turns
// that answer it from EVERY project. Both levels run the same list against the
// same route; only the scope differs. That matters because the filter is applied
// SERVER-side to the whole store and the 50-row page is cut from what it matched:
// filtering a page instead would report "that is all of them" off a cursor that
// had never been past the newest 50 turns.

import { useEffect, useMemo, useState } from "react";
import {
  Button,
  type DateRange,
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  Input,
  isDateRangeActive,
  MultiSelect,
  type MultiSelectOption,
} from "@recruiter/shared";
import type { DebugProjectDTO } from "@recruiter/shared/types";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { AreaBoundary } from "../components/area-gate";
import { DebugChat } from "../components/debug-chat";
import { DebugRunCard } from "../components/debug-run-card";
import { EmptyState, LoadingState } from "../components/states";
import {
  AGENT_BLURB,
  DEBUG_AGENTS,
  type DevelopersMode,
  agentLabel,
  isReplayTurn,
} from "../lib/debug-agents";
import { formatDateTime } from "../lib/format";
import { useDebounced } from "../lib/use-debounced";
import {
  type DebugTurnScope,
  debugAgentFeedQuery,
  debugProjectsQuery,
  debugTurnQuery,
  debugTurnsQuery,
} from "../lib/queries";
import "./developers.css";

/** The URL value for the synthetic "turns with no project" bucket. A real project
 *  id is a uuid, so this can never collide with one. */
export const NO_PROJECT = "none";

export interface DevelopersPageProps {
  /** Project search text (server-side). */
  q: string;
  onQChange: (next: string) => void;
  /** Selected project id, or `NO_PROJECT`, or undefined for the projects list. */
  project?: string;
  onProjectChange: (next: string | undefined) => void;
  turn?: string;
  onTurnChange: (next: string | undefined) => void;
  agent: string;
  onAgentChange: (next: string) => void;
  /** The turns-list filter: show only turns in which at least one of these
   *  agents fired. Empty is no filter. Distinct from `agent`, which picks which
   *  TAB you are reading on the right. */
  agents: readonly string[];
  onAgentsChange: (next: readonly string[]) => void;
  mode: DevelopersMode;
  onModeChange: (next: DevelopersMode) => void;
}

/** The filter's options: the full roster, not the agents present on the loaded
 *  page. The list narrows server-side across every turn, so deriving options
 *  from the rows in memory would hide an agent whose turns are all older than
 *  the first page — and this vocabulary is one the bundle owns (it mirrors the
 *  backend's `DISPLAY_AGENTS`), which is the case `lib/table-filter.ts` allows a
 *  fixed list for. */
const AGENT_FILTER_OPTIONS: readonly MultiSelectOption[] = DEBUG_AGENTS.map((a) => ({
  value: a,
  label: agentLabel(a),
}));

/** A readable owner label from the resolved user, falling back to nothing (the
 *  callers each have their own fallback for a row with no resolvable owner). */
function ownerLabel(o: { owner_display_name: string | null; owner_email: string | null }) {
  return o.owner_display_name ?? o.owner_email ?? null;
}

/** Readable name for a project row: its name, else its raw id, else the bucket. */
function projectName(p: DebugProjectDTO): string {
  return p.project_name ?? p.project_id ?? "No project";
}

// ── The rail's filter strip ─────────────────────────────────────────────────

/** WHICH agents fired in a turn, and WHEN it ran.
 *
 *  Both narrow SERVER-side (the rail is keyset-paged, so narrowing the page
 *  already in the browser would hide every match further back), and both sit at
 *  BOTH rail levels: the same pair filters every captured turn before a project
 *  is picked, and that one project's turns after. The agent selection is a URL
 *  param because it names what somebody is looking at, so a filtered rail is a
 *  link you can send; the date range is not, because it narrows the rail rather
 *  than naming the trace on screen. */
function RailFilters({
  agents,
  onAgentsChange,
  range,
  onRangeChange,
}: {
  agents: readonly string[];
  onAgentsChange: (next: readonly string[]) => void;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
}) {
  return (
    <div className="dbg-rail__filters">
      <MultiSelect
        label="Filter turns by agent"
        allLabel="All agents"
        options={AGENT_FILTER_OPTIONS}
        selected={agents}
        onChange={onAgentsChange}
      />
      <DateRangeFilter label="Turn ran" value={range} onChange={onRangeChange} />
    </div>
  );
}

// ── The rail's turn list ────────────────────────────────────────────────────

/** The paged turn list, shared by both rail levels.
 *
 *  The filter runs over the SCOPE'S WHOLE STORE and the page is cut from what it
 *  matched, so these 50 rows are the 50 newest MATCHES, never the matches that
 *  happened to fall inside a page fetched first. `Older turns` then walks back
 *  through that same filtered list by the oldest `ended_at` loaded. Getting it
 *  the other way round is the bug this rail is arranged to avoid: a filter
 *  applied to a page reports "that is all of them" while sitting on a cursor that
 *  has never been past the first 50 rows. */
function TurnList({
  scope,
  agents,
  range,
  q = "",
  showProject = false,
  selectedTurn,
  onSelectTurn,
  onClearFilters,
}: {
  scope: DebugTurnScope;
  agents: readonly string[];
  range: DateRange;
  /** Server-side search over turn id, project id/name, buyer id, and owner. */
  q?: string;
  /** Name each turn's project on the row: noise in a list that is already one
   *  project's, and half the answer in a list that spans every project. */
  showProject?: boolean;
  selectedTurn: string | undefined;
  onSelectTurn: (turnId: string) => void;
  onClearFilters: () => void;
}) {
  const turns = useInfiniteQuery(debugTurnsQuery(scope, agents, range, q));
  const rows = useMemo(() => turns.data?.pages.flatMap((p) => p.turns) ?? [], [turns.data]);
  const filtered = agents.length > 0 || isDateRangeActive(range) || q.trim().length > 0;

  // Land on the newest turn once the list arrives and nothing is chosen, so
  // neither a drill-down nor a filter picked at the top level leaves the
  // right-hand pane empty. Deliberately does NOT re-point at the filtered list's
  // newest turn: narrowing the rail is a navigation aid, and yanking the trace
  // out from under a reader mid-read is not what a filter is for. A selected turn
  // the filter excludes simply stops being highlighted; the URL still names it
  // and the right pane still reads it.
  useEffect(() => {
    if (!selectedTurn && rows.length > 0) onSelectTurn(rows[0].turn_id);
  }, [rows, selectedTurn, onSelectTurn]);

  if (turns.isPending) return <p className="dbg-muted">Loading turns…</p>;
  if (turns.isError) {
    return (
      <AreaBoundary area="developers" error={turns.error} onRetry={() => void turns.refetch()} />
    );
  }
  if (rows.length === 0) {
    // Say WHICH question came back empty. "No turns captured" under an active
    // filter reads as "this never ran", which is a different and much more
    // alarming claim.
    if (!filtered) {
      return (
        <p className="dbg-muted">
          {scope.kind === "all"
            ? "No agent activity captured yet."
            : "No turns captured for this project."}
        </p>
      );
    }
    return (
      <p className="dbg-muted">
        {agents.length > 0 ? `No turns matching ${agents.map(agentLabel).join(", ")}` : "No turns"}
        {isDateRangeActive(range) ? " in these dates" : ""}
        {q.trim() ? ` for ${q.trim()}` : ""}
        {scope.kind === "all" ? ", in any project" : ""}.{" "}
        <button type="button" className="dbg-linkish" onClick={onClearFilters}>
          Clear filters
        </button>
      </p>
    );
  }
  return (
    <>
      {rows.map((t) => {
        const who = ownerLabel(t) ?? t.buyer_id ?? null;
        return (
          <button
            key={t.turn_id}
            type="button"
            className={`dbg-turn${selectedTurn === t.turn_id ? " is-selected" : ""}`}
            onClick={() => onSelectTurn(t.turn_id)}
          >
            <span className="dbg-turn__meta">
              {formatDateTime(t.ended_at)} · {t.call_count} call
              {t.call_count === 1 ? "" : "s"} · ${t.total_cost_usd.toFixed(4)}
            </span>
            {/* An Agent-Rerunner replay traces exactly like a live search, so
                without this badge it reads as a second real search on the
                conversation. The `turn_rerun_` prefix in the id below is the only
                other tell, and it is a small slice of a long hex string nobody
                parses at a glance. */}
            {isReplayTurn(t.turn_id) ? (
              <span
                className="dbg-turn__replay"
                title="Agent-Rerunner replay: the real SCOUT loop re-run over this project's brief. It wrote no search row and contacted nobody, so it is not a search the client saw."
              >
                Replay
              </span>
            ) : null}
            {showProject ? (
              <span className="dbg-turn__project">
                {t.project_name ?? t.project_id ?? "No project"}
              </span>
            ) : null}
            {who ? <span className="dbg-turn__user">{who}</span> : null}
            <span className="dbg-turn__id">{t.turn_id}</span>
            <span className="dbg-turn__agents">
              {t.agents.map((a) => (
                <span key={a} className="dbg-chip">
                  {agentLabel(a)}
                </span>
              ))}
            </span>
          </button>
        );
      })}
      {turns.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          tone="neutral"
          size="sm"
          disabled={turns.isFetchingNextPage}
          onClick={() => void turns.fetchNextPage()}
        >
          {turns.isFetchingNextPage ? "Loading…" : "Older turns"}
        </Button>
      ) : null}
    </>
  );
}

// ── Left rail, level one: the projects list, or every project's turns ───────

function ProjectsRail({
  q,
  onQChange,
  agents,
  onAgentsChange,
  range,
  onRangeChange,
  selectedTurn,
  onSelectTurn,
  onPick,
}: {
  q: string;
  onQChange: (next: string) => void;
  agents: readonly string[];
  onAgentsChange: (next: readonly string[]) => void;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  selectedTurn: string | undefined;
  onSelectTurn: (turnId: string) => void;
  onPick: (project: DebugProjectDTO) => void;
}) {
  const debouncedQ = useDebounced(q.trim(), 300);

  // A filter is a question about TURNS, not about projects, so picking one flips
  // this level from the project index to the turns that answer it: across every
  // project, newest first, paged. Clearing it puts the index back. Without the
  // flip the agent filter could only ever reach the turns of whichever project
  // was already open, which is not the scope of "show me SCOUT" and made the
  // answer look like there were far fewer such turns than there are.
  const filtering = agents.length > 0 || isDateRangeActive(range);
  const projects = useQuery({ ...debugProjectsQuery(debouncedQ), enabled: !filtering });

  return (
    <>
      <Input
        type="search"
        inputSize="sm"
        placeholder={
          filtering
            ? "Search turns: project, user, or turn id…"
            : "Search projects: name, user, or id…"
        }
        value={q}
        onChange={(event) => onQChange(event.target.value)}
        aria-label={filtering ? "Search turns" : "Search projects"}
      />
      <RailFilters
        agents={agents}
        onAgentsChange={onAgentsChange}
        range={range}
        onRangeChange={onRangeChange}
      />
      <div className="dbg-rail__list">
        {filtering ? (
          <TurnList
            scope={{ kind: "all" }}
            agents={agents}
            range={range}
            q={debouncedQ}
            showProject
            selectedTurn={selectedTurn}
            onSelectTurn={onSelectTurn}
            onClearFilters={() => {
              onAgentsChange([]);
              onRangeChange(EMPTY_DATE_RANGE);
            }}
          />
        ) : projects.isPending ? (
          <p className="dbg-muted">Loading projects…</p>
        ) : projects.isError ? (
          <AreaBoundary
            area="developers"
            error={projects.error}
            onRetry={() => void projects.refetch()}
          />
        ) : projects.data.projects.length === 0 ? (
          <p className="dbg-muted">
            {q.trim()
              ? "No projects match your search."
              : "No agent activity captured yet. Run an agent turn, then refresh."}
          </p>
        ) : (
          projects.data.projects.map((p) => {
            const isBucket = p.project_id === null;
            const owner = ownerLabel(p);
            return (
              <button
                key={p.project_id ?? NO_PROJECT}
                type="button"
                className={`dbg-proj${isBucket ? " dbg-proj--bucket" : ""}`}
                onClick={() => onPick(p)}
              >
                <span className="dbg-proj__name">{projectName(p)}</span>
                <span className="dbg-proj__owner">
                  {isBucket ? "system · various" : (owner ?? p.buyer_id ?? "Unknown user")}
                </span>
                <span className="dbg-proj__meta">
                  {formatDateTime(p.last_activity_at)} · {p.turn_count} turn
                  {p.turn_count === 1 ? "" : "s"} · ${p.total_cost_usd.toFixed(4)}
                </span>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}

// ── Left rail, level two: one project's turns ───────────────────────────────

function TurnsRail({
  scope,
  agents,
  onAgentsChange,
  range,
  onRangeChange,
  selectedTurn,
  onSelectTurn,
  onBack,
}: {
  scope: DebugTurnScope;
  agents: readonly string[];
  onAgentsChange: (next: readonly string[]) => void;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  selectedTurn: string | undefined;
  onSelectTurn: (turnId: string) => void;
  onBack: () => void;
}) {
  return (
    <>
      <button type="button" className="dbg-back" onClick={onBack}>
        ← All projects
      </button>
      <RailFilters
        agents={agents}
        onAgentsChange={onAgentsChange}
        range={range}
        onRangeChange={onRangeChange}
      />
      <div className="dbg-rail__list">
        <TurnList
          scope={scope}
          agents={agents}
          range={range}
          selectedTurn={selectedTurn}
          onSelectTurn={onSelectTurn}
          onClearFilters={() => {
            onAgentsChange([]);
            onRangeChange(EMPTY_DATE_RANGE);
          }}
        />
      </div>
    </>
  );
}

// ── Right pane, scoped mode: the chosen turn, tab per agent ─────────────────

function ScopedRuns({
  turnId,
  agent,
  onFiredAgents,
}: {
  turnId: string;
  agent: string;
  onFiredAgents: (agents: ReadonlySet<string>) => void;
}) {
  const runs = useQuery(debugTurnQuery(turnId));
  const all = useMemo(() => runs.data?.runs ?? [], [runs.data]);

  // Lift which agents fired, so the tab strip can dot them.
  useEffect(() => {
    onFiredAgents(new Set(all.map((r) => r.agent)));
  }, [all, onFiredAgents]);

  if (runs.isPending) return <LoadingState label="Loading turn…" />;
  if (runs.isError) {
    return (
      <AreaBoundary area="developers" error={runs.error} onRetry={() => void runs.refetch()} />
    );
  }

  const forAgent = all.filter((r) => r.agent === agent);
  if (forAgent.length === 0) {
    return (
      <div className="dbg-empty">
        <p>
          <b>{agentLabel(agent)}</b> didn&rsquo;t run this turn.
        </p>
        <p className="dbg-muted">{AGENT_BLURB[agent]}</p>
      </div>
    );
  }
  return (
    <>
      {forAgent.map((run, i) => (
        <DebugRunCard key={`${run.turn_id}-${run.agent}-${i}`} run={run} defaultOpen />
      ))}
    </>
  );
}

// ── Right pane, feed mode: one agent across every turn ──────────────────────

function AgentFeed({ agent }: { agent: string }) {
  const feed = useInfiniteQuery(debugAgentFeedQuery(agent));
  const runs = feed.data?.pages.flatMap((p) => p.runs) ?? [];

  if (feed.isPending) return <LoadingState label={`Loading ${agentLabel(agent)} runs…`} />;
  if (feed.isError) {
    return (
      <AreaBoundary area="developers" error={feed.error} onRetry={() => void feed.refetch()} />
    );
  }
  if (runs.length === 0) {
    return (
      <div className="dbg-empty">
        <p>
          No recent runs for <b>{agentLabel(agent)}</b>.
        </p>
        <p className="dbg-muted">{AGENT_BLURB[agent]}</p>
      </div>
    );
  }
  return (
    <>
      {runs.map((run, i) => (
        <DebugRunCard key={`${run.turn_id}-${i}`} run={run} />
      ))}
      {feed.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          tone="neutral"
          size="sm"
          disabled={feed.isFetchingNextPage}
          onClick={() => void feed.fetchNextPage()}
        >
          {feed.isFetchingNextPage ? "Loading…" : "Older runs"}
        </Button>
      ) : null}
    </>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

export function DevelopersPage({
  q,
  onQChange,
  project,
  onProjectChange,
  turn,
  onTurnChange,
  agent,
  onAgentChange,
  agents,
  onAgentsChange,
  mode,
  onModeChange,
}: DevelopersPageProps) {
  const [firedAgents, setFiredAgents] = useState<ReadonlySet<string>>(new Set());

  // WHEN a turn ran. Held HERE rather than inside a rail so it survives the
  // drill-down: the agent selection is a URL param that already does, and a range
  // that reset every time you opened a project would keep re-widening half of the
  // same question. Not a URL param itself, for the reason `RailFilters` gives.
  const [range, setRange] = useState<DateRange>(EMPTY_DATE_RANGE);

  const scope = useMemo<DebugTurnScope | null>(() => {
    if (!project) return null;
    return project === NO_PROJECT ? { kind: "none" } : { kind: "project", projectId: project };
  }, [project]);

  // The tab dots describe the SCOPED turn only; in feed mode there is no one turn
  // for them to describe, and a stale set would claim a turn that isn't on screen.
  const dotted = mode === "scoped" ? firedAgents : new Set<string>();

  return (
    <div className="ad-page">
      <header className="ad-page__head ad-page__head--row">
        <div>
          <h1 className="ad-page__title">Agent debug</h1>
          <p className="ad-page__sub">
            Every LLM call each agent made: what it was given, what it answered, and what it cost.
            Ask the trace why, in the corner.
          </p>
        </div>
        <div className="dbg-modeswitch" role="group" aria-label="Debug view">
          <button
            type="button"
            className={mode === "scoped" ? "is-active" : ""}
            aria-pressed={mode === "scoped"}
            onClick={() => onModeChange("scoped")}
          >
            Scoped to turn
          </button>
          <button
            type="button"
            className={mode === "feed" ? "is-active" : ""}
            aria-pressed={mode === "feed"}
            onClick={() => onModeChange("feed")}
          >
            Global feed
          </button>
        </div>
      </header>

      <div className="dbg-layout">
        <aside className="dbg-rail">
          {scope === null ? (
            <ProjectsRail
              q={q}
              onQChange={onQChange}
              agents={agents}
              onAgentsChange={onAgentsChange}
              range={range}
              onRangeChange={setRange}
              selectedTurn={turn}
              // Picking a turn out of the whole-store list does NOT drill into its
              // project: the filtered list is the thing being read, and collapsing
              // it to one project on every click would throw away the answer to
              // put you back where the filter could not reach.
              onSelectTurn={(turnId) => {
                onTurnChange(turnId);
                onModeChange("scoped");
              }}
              onPick={(p) => {
                onProjectChange(p.project_id ?? NO_PROJECT);
                onTurnChange(undefined);
                onModeChange("scoped");
              }}
            />
          ) : (
            <TurnsRail
              scope={scope}
              agents={agents}
              onAgentsChange={onAgentsChange}
              range={range}
              onRangeChange={setRange}
              selectedTurn={turn}
              onSelectTurn={(turnId) => {
                onTurnChange(turnId);
                onModeChange("scoped");
              }}
              onBack={() => {
                onProjectChange(undefined);
                onTurnChange(undefined);
              }}
            />
          )}
        </aside>

        <section className="dbg-main">
          <nav className="dbg-tabs" aria-label="Agents">
            {DEBUG_AGENTS.map((a) => (
              <button
                key={a}
                type="button"
                className={`dbg-tab${agent === a ? " is-active" : ""}`}
                aria-pressed={agent === a}
                onClick={() => onAgentChange(a)}
              >
                {agentLabel(a)}
                {dotted.has(a) ? <span className="dbg-tab__dot" /> : null}
              </button>
            ))}
          </nav>

          <div className="dbg-content">
            {/* A chosen turn is read whether or not a project is chosen: the rail
                can now name a turn from the whole-store list, where there is no
                project selection to have made first. */}
            {mode === "feed" ? (
              <AgentFeed agent={agent} />
            ) : turn ? (
              <ScopedRuns turnId={turn} agent={agent} onFiredAgents={setFiredAgents} />
            ) : project ? (
              <EmptyState title="Pick a turn" message="Choose a turn on the left to read it." />
            ) : (
              <EmptyState
                title="Pick a project"
                message="Choose a project on the left, or filter by agent to search every project's turns."
              />
            )}
          </div>
        </section>
      </div>

      <DebugChat targetTurnId={mode === "scoped" ? (turn ?? null) : null} />
    </div>
  );
}
